import PropTypes from 'prop-types';
import React, { memo, useMemo } from 'react';
import { List, Map } from 'immutable';
import classNames from 'classnames';

import Table, { Column, Row } from '../../helpers/Table';
import config from '../../../config';
import Input from '../../helpers/Input';
import { unwrap } from '../../../utils/ChangeSpy';
import FormSubmitOnBlur from '../../helpers/FormSubmitOnBlur';
import { Card } from '../../helpers/Card';
import { Spinner } from '../../helpers/Spinner';
import { IconButton } from '../../helpers/IconButton';

import style from './style.module.css';

/**
 * Target panel component.
 *
 * @param props {Object}.
 * @param props.targetColumns {Immutable.List} Target column configuration.
 * @param props.targets {Immutable.List} Target List.
 * @param props.common {Immutable.Map} Common Object.
 * @param props.onSort {Function} Handle event of sorting table.
 * @param props.onChange {Function}.
 * @param props.onSubmit {Function}.
 */
const TargetTableComponent = ({
  targetColumns,
  targets,
  common,
  projectData,
  onDoubleClick,
  onSort,
  onChange,
  onSubmit,
  onTableRef,
  onCeoProjectTargetChange,
}) => {
  const type = config.values.getIn(['project', 'types', 'TARGETS']);
  const columns = getColumns(
    targetColumns,
    common.get('targetSortField'),
    common.get('targetSortDirection'),
    onSort,
    type,
    onChange,
    onSubmit,
  );
  const label = useMemo(() => {
    const buyerName = projectData.get('buyerName');
    const name = projectData.getIn(['category', 'name']);

    return (
      <div className={style.universe}>
        <span className={style.universeTitle}>Universe</span>
        <label className={style.option}>
          <input name="active" onChange={onCeoProjectTargetChange} type="radio" value="0" defaultChecked />
          {buyerName} - {name} is the &quot;Active&quot; Buyer - Project
        </label>
        <label className={style.option}>
          <input name="active" onChange={onCeoProjectTargetChange} type="radio" value="1" />
          ALL Targets that have been assigned to {buyerName} - {name} - Project
        </label>
      </div>
    );
  }, [projectData, onCeoProjectTargetChange]);

  const spinner = common.get('targetLoading') ? <Spinner /> : null;

  return (
    <Card className={style.host} flush>
      {label}
      <div ref={onTableRef} className={style.tableWrapper}>
        {spinner}
        <Table className={classNames('table table-striped', style.table)}>
          {columns}
          {targets.map((target, i) => (
            <Row
              key={target.get('id')}
              data={target.set('i', i + 1)}
              onDoubleClick={() => onDoubleClick({ row: target, type })}
            />
          ))}
        </Table>
      </div>
    </Card>
  );
};

TargetTableComponent.propTypes = {
  common: PropTypes.instanceOf(Map).isRequired,
  onSort: PropTypes.func.isRequired,
  targetColumns: PropTypes.instanceOf(List).isRequired,
  targets: PropTypes.instanceOf(List).isRequired,
};

export const TargetTable = memo(TargetTableComponent);

/**
 * Generate table columns.
 *
 * @param targetColumns {Immutable.List} Target column configuration.
 * @param field {Immutable.List} Target List.
 * @param direction {String} Direction of sorting.
 * @param onClick {Function} Handle event of clicking on column head.
 * @param type {String} Type of table.
 * @param onChange {Function} Handle event of change.
 * @param onSubmit {Function} Handle event of submit.
 */
function getColumns(targetColumns, field, direction, onClick, type, onChange, onSubmit) {
  return targetColumns.map((column, index) => {
    const columnField = column.get('field');
    const sortable = column.get('sortable');

    if (columnField === 'approved') {
      return drawApprovedColumn(
        index,
        column.get('headerName'),
        columnField,
        onClick,
        columnField === field ? direction : null,
        type,
        onChange,
        onSubmit,
      );
    }

    if (columnField === 'priority') {
      return drawPriorityColumn(
        index,
        column.get('headerName'),
        columnField,
        onClick,
        columnField === field ? direction : null,
        type,
        onChange,
        onSubmit,
      );
    }

    if (columnField === 'currentProjectCategory') {
      return drawProjectCategoryColumn(
        index,
        column.get('headerName'),
        columnField,
        onClick,
        columnField === field ? direction : null,
        type,
        sortable,
      );
    }

    return drawColumn(
      index,
      column.get('headerName'),
      columnField,
      onClick,
      columnField === field ? direction : null,
      type,
      sortable,
    );
  });
}

/**
 * Draw column head.
 *
 * @param key {number} Column key.
 * @param headerName {String} Column title.
 * @param field {String} Sorting field name.
 * @param onClick {Function} Handle event of clicking on column head.
 * @param direction {String} Direction of sorting.
 * @param type {String} Type of table.
 * @param sortable {Boolean} Is sortable.
 */
function drawColumn(key, headerName, field, onClick, direction, type, sortable) {
  if (!sortable) {
    return <Column key={key} field={field} title={<span className="text-regular">{headerName}</span>} />;
  }

  return (
    <Column
      key={key}
      field={field}
      title={
        <a
          className="text-regular"
          onClick={event =>
            onClick(event, {
              field,
              direction: direction === 'up' ? 'down' : 'up',
              type,
            })
          }
        >
          {headerName} {direction ? <i aria-hidden="true" className={`fa fa-caret-${direction}`} /> : null}
        </a>
      }
    />
  );
}

/**
 * Generate True|False value column.
 *
 * @param key {number} Column key.
 * @param headerName {String} Column title.
 * @param field {String} Sorting field name.
 * @param onClick {Function} Handle event of clicking on column head.
 * @param onChange {Function} Handle event of changing.
 * @param onSubmit {Function} Handle event of submitting.
 * @param direction {String} Direction of sorting.
 * @param type {String} Type of table.
 */
function drawApprovedColumn(key, headerName, field, onClick, direction, type, onChange, onSubmit) {
  const change = (event, value, data) => {
    onChange({ target: { value: true } }, data, 'processing');
    onChange({ target: { value } }, data, 'approved');
    onSubmit(event, {
      field: 'approved',
      tbId: data.get('tbId'),
      id: data.get('id'),
      approved: value,
    });
  };

  return (
    <Column
      key={key}
      className={style.priorityColumn}
      field={field}
      title={
        <a
          className="text-regular"
          onClick={event =>
            onClick(event, {
              field,
              direction: direction === 'up' ? 'down' : 'up',
              type,
            })
          }
        >
          {headerName} {direction ? <i aria-hidden="true" className={`fa fa-caret-${direction}`} /> : null}
        </a>
      }
      valueWrapper={(value, data) => {
        const approved = unwrap(value);

        if (data.get('processing')) {
          return <Spinner className="text-primary" size="small" inline />;
        }

        if (approved === true) {
          return (
            <IconButton
              icon="check"
              onClick={event => change(event, null, data)}
              size="sm"
              title="Approved, click to reset"
              variant="success"
              active
            />
          );
        }

        if (approved === false) {
          return (
            <IconButton
              icon="times"
              onClick={event => change(event, null, data)}
              size="sm"
              title="Rejected, click to reset"
              variant="danger"
              active
            />
          );
        }

        return (
          <div className="d-flex">
            <IconButton
              icon="check"
              onClick={event => change(event, true, data)}
              size="sm"
              title="Approve"
              variant="success"
            />
            <IconButton
              icon="times"
              onClick={event => change(event, false, data)}
              size="sm"
              title="Reject"
              variant="danger"
            />
          </div>
        );
      }}
    />
  );
}

/**
 * Generate True|False value column.
 *
 * @param key {number} Column key.
 * @param headerName {String} Column title.
 * @param field {String} Sorting field name.
 * @param onClick {Function} Handle event of clicking on column head.
 * @param direction {String} Direction of sorting.
 * @param type {String} Type of table.
 * @param onChange {Function} Handle event of changing.
 * @param onSubmit {Function} Handle event of submitting.
 */
function drawPriorityColumn(key, headerName, field, onClick, direction, type, onChange, onSubmit) {
  return (
    <Column
      key={key}
      className={style.priorityColumn}
      field={field}
      title={
        <a
          className="text-regular"
          onClick={event =>
            onClick(event, {
              field,
              direction: direction === 'up' ? 'down' : 'up',
              type,
            })
          }
        >
          {headerName} {direction ? <i aria-hidden="true" className={`fa fa-caret-${direction}`} /> : null}
        </a>
      }
      valueWrapper={(value, i) => (
        <FormSubmitOnBlur className="mr5" onSubmit={onSubmit} focusOnClick>
          <Input className="form-control input-sm" onChange={event => onChange(event, i, field)} value={value || ''} />
        </FormSubmitOnBlur>
      )}
    />
  );
}

/**
 * Draw project category head.
 *
 * @param key {number} Column key.
 * @param headerName {String} Column title.
 * @param field {String} Sorting field name.
 * @param onClick {Function} Handle event of clicking on column head.
 * @param direction {String} Direction of sorting.
 * @param type {String} Type of table.
 * @param sortable {Boolean} Is sortable.
 */
function drawProjectCategoryColumn(key, headerName, field, onClick, direction, type, sortable) {
  if (!sortable) {
    return <Column key={key} field={field} title={<span className="text-regular">{headerName}</span>} />;
  }

  return (
    <Column
      key={key}
      field={field}
      title={
        <a
          className="text-regular"
          onClick={event =>
            onClick(event, {
              field,
              direction: direction === 'up' ? 'down' : 'up',
              type,
            })
          }
        >
          {headerName} {direction ? <i aria-hidden="true" className={`fa fa-caret-${direction}`} /> : null}
        </a>
      }
      valueWrapper={(value, i) => (
        <a href={`/project/${i.get('currentProjectId')}`} rel="noreferrer" target="_blank">
          {value}
        </a>
      )}
    />
  );
}
